import { Option, STATUSPAGE } from '@repo/ui';
import {
  CourseListUseCaseInterface,
  GetAllGroupsUserUseCaseInterface,
} from 'domain/usecases';
import { RequestState } from 'presentation/app/common/interfaces/request-state';
import { useService } from 'presentation/app/hooks/useService';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ActiveTcesSection } from './active-tces-section';
import { ExpiredTcesSection } from './expired-tces-section';
import { StudentsWithoutTceSection } from './students-without-tce-section';
import { SummariesSection } from './summaries-section';
import { TcesDraftsSection } from './tces-drafts-section';
import { TcesSentToIntegrativeSection } from './tces-sent-to-integrative-section';
import { TcesWaitingGrantorSignatureSection } from './tces-waiting-grantor-signature-section';
import { TcesWaitingInstitutionSignatureSection } from './tces-waiting-institution-signature-section';
import { TcesWaitingStudentSignatureSection } from './tces-waiting-student-signature-section';

export type TcesTabSectionId =
  | 'summaries'
  | 'studentsWithoutTCE'
  | 'drafts'
  | 'waitingStudentSignature'
  | 'waitingGrantorSignature'
  | 'waitingInstitutionSignature'
  | 'sentToIntegrative'
  | 'active'
  | 'expired';

interface IProps {
  entityId: string;
}

export function TcesTabContent({ entityId }: IProps) {
  // Hooks
  const { groupsService, courseService } = useService();

  // States
  const [selectedTcesTabSectionId, setSelectedTcesTabSectionId] =
    useState<TcesTabSectionId>('summaries');
  const [getOrganizationCoursesRequest, setGetOrganizationCoursesRequest] =
    useState<RequestState<CourseListUseCaseInterface.Result[]>>({
      data: undefined,
      status: STATUSPAGE.INITIAL,
    });
  const [getOrganizationGroupsRequest, setGetOrganizationGroupsRequest] =
    useState<RequestState<GetAllGroupsUserUseCaseInterface.Result>>({
      data: undefined,
      status: STATUSPAGE.INITIAL,
    });

  // Methods
  const changeSelectedTcesTabSectionId = useCallback(
    (sectionId: TcesTabSectionId) => {
      setSelectedTcesTabSectionId(sectionId);
    },
    [],
  );

  const courseOptions = useMemo<Option[]>(() => {
    const data = getOrganizationCoursesRequest.data;
    if (!data || data.length === 0) {
      return [];
    }

    return data.map((course) => ({
      label: course.nome,
      value: course.id,
    }));
  }, [entityId, getOrganizationCoursesRequest.data]);

  const groupOptions = useMemo(() => {
    const data = getOrganizationGroupsRequest.data;
    if (!data || data.length === 0) {
      return [];
    }

    return data.map((group) => ({
      label: group.nome,
      value: group.id,
    }));
  }, [entityId, getOrganizationGroupsRequest.data]);

  const requestGetOrganizationCourses = useCallback(() => {
    setGetOrganizationCoursesRequest((prevState) => ({
      ...prevState,
      status: STATUSPAGE.LOADING,
    }));
    courseService
      .courseList({ entityId })
      .then((res) => {
        setGetOrganizationCoursesRequest((prevState) => ({
          ...prevState,
          data: res,
        }));
      })
      .catch((err) => {
        setGetOrganizationCoursesRequest((prevState) => ({
          ...prevState,
          data: undefined,
        }));
      })
      .finally(() => {
        setGetOrganizationCoursesRequest((prevState) => ({
          ...prevState,
          status: STATUSPAGE.DONE,
        }));
      });
  }, [entityId]);

  const requestGetOrganizationGroups = useCallback(() => {
    setGetOrganizationGroupsRequest((prevState) => ({
      ...prevState,
      status: STATUSPAGE.LOADING,
    }));
    groupsService
      .getAllGroupsUser({ entityId })
      .then((res) => {
        setGetOrganizationGroupsRequest((prevState) => ({
          ...prevState,
          data: res,
        }));
      })
      .catch((err) => {
        setGetOrganizationGroupsRequest((prevState) => ({
          ...prevState,
          data: undefined,
        }));
      })
      .finally(() => {
        setGetOrganizationGroupsRequest((prevState) => ({
          ...prevState,
          status: STATUSPAGE.DONE,
        }));
      });
  }, [entityId]);

  const renderTcesTabSectionComponent = useCallback(
    (sectionId: TcesTabSectionId) => {
      switch (sectionId) {
        case 'summaries':
          return (
            <SummariesSection
              entityId={entityId}
              changeSelectedTcesTabSectionId={changeSelectedTcesTabSectionId}
            />
          );
        case 'studentsWithoutTCE':
          return (
            <StudentsWithoutTceSection
              courseOptions={courseOptions}
              groupOptions={groupOptions}
              entityId={entityId}
              changeSelectedTcesTabSectionId={changeSelectedTcesTabSectionId}
              isCourseSelectLoading={
                getOrganizationCoursesRequest.status === STATUSPAGE.LOADING
              }
              isGroupSelectLoading={
                getOrganizationGroupsRequest.status === STATUSPAGE.LOADING
              }
            />
          );
        case 'drafts':
          return (
            <TcesDraftsSection
              courseOptions={courseOptions}
              groupOptions={groupOptions}
              entityId={entityId}
              changeSelectedTcesTabSectionId={changeSelectedTcesTabSectionId}
              isCourseSelectLoading={
                getOrganizationCoursesRequest.status === STATUSPAGE.LOADING
              }
              isGroupSelectLoading={
                getOrganizationGroupsRequest.status === STATUSPAGE.LOADING
              }
            />
          );
        case 'waitingStudentSignature':
          return (
            <TcesWaitingStudentSignatureSection
              courseOptions={courseOptions}
              groupOptions={groupOptions}
              entityId={entityId}
              changeSelectedTcesTabSectionId={changeSelectedTcesTabSectionId}
              isCourseSelectLoading={
                getOrganizationCoursesRequest.status === STATUSPAGE.LOADING
              }
              isGroupSelectLoading={
                getOrganizationGroupsRequest.status === STATUSPAGE.LOADING
              }
            />
          );
        case 'waitingInstitutionSignature':
          return (
            <TcesWaitingInstitutionSignatureSection
              courseOptions={courseOptions}
              groupOptions={groupOptions}
              entityId={entityId}
              changeSelectedTcesTabSectionId={changeSelectedTcesTabSectionId}
              isCourseSelectLoading={
                getOrganizationCoursesRequest.status === STATUSPAGE.LOADING
              }
              isGroupSelectLoading={
                getOrganizationGroupsRequest.status === STATUSPAGE.LOADING
              }
            />
          );
        case 'waitingGrantorSignature':
          return (
            <TcesWaitingGrantorSignatureSection
              courseOptions={courseOptions}
              groupOptions={groupOptions}
              entityId={entityId}
              changeSelectedTcesTabSectionId={changeSelectedTcesTabSectionId}
              isCourseSelectLoading={
                getOrganizationCoursesRequest.status === STATUSPAGE.LOADING
              }
              isGroupSelectLoading={
                getOrganizationGroupsRequest.status === STATUSPAGE.LOADING
              }
            />
          );
        case 'sentToIntegrative':
          return (
            <TcesSentToIntegrativeSection
              courseOptions={courseOptions}
              groupOptions={groupOptions}
              entityId={entityId}
              changeSelectedTcesTabSectionId={changeSelectedTcesTabSectionId}
              isCourseSelectLoading={
                getOrganizationCoursesRequest.status === STATUSPAGE.LOADING
              }
              isGroupSelectLoading={
                getOrganizationGroupsRequest.status === STATUSPAGE.LOADING
              }
            />
          );
        case 'active':
          return (
            <ActiveTcesSection
              courseOptions={courseOptions}
              groupOptions={groupOptions}
              entityId={entityId}
              changeSelectedTcesTabSectionId={changeSelectedTcesTabSectionId}
              isCourseSelectLoading={
                getOrganizationCoursesRequest.status === STATUSPAGE.LOADING
              }
              isGroupSelectLoading={
                getOrganizationGroupsRequest.status === STATUSPAGE.LOADING
              }
            />
          );
        case 'expired':
          return (
            <ExpiredTcesSection
              courseOptions={courseOptions}
              groupOptions={groupOptions}
              entityId={entityId}
              changeSelectedTcesTabSectionId={changeSelectedTcesTabSectionId}
              isCourseSelectLoading={
                getOrganizationCoursesRequest.status === STATUSPAGE.LOADING
              }
              isGroupSelectLoading={
                getOrganizationGroupsRequest.status === STATUSPAGE.LOADING
              }
            />
          );
        default:
          return (
            <SummariesSection
              entityId={entityId}
              changeSelectedTcesTabSectionId={changeSelectedTcesTabSectionId}
            />
          );
      }
    },
    [
      entityId,
      getOrganizationCoursesRequest.status,
      getOrganizationGroupsRequest.status,
      courseOptions,
      groupOptions,
    ],
  );

  useEffect(() => {
    requestGetOrganizationCourses();
    requestGetOrganizationGroups();
  }, [entityId]);

  return (
    <div className="mt-5">
      {renderTcesTabSectionComponent(selectedTcesTabSectionId)}
    </div>
  );
}
